import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { findMarqueeAnimation } from '../../findMarqueeAnimation';

/** How long a full 1 -> 0 (or 0 -> 1) ramp takes; a partial ramp is scaled down to match speed. */
const RAMP_DURATION_MS = 450;

/* How long the pointer has to settle before the pause begins. A pointer crossing the row on its
   way elsewhere would otherwise start a ramp and immediately reverse it, and since a reversed
   ramp is shortened to match speed, each crossing snaps the track back - several in a row read
   as the marquee jittering rather than easing. */
const HOVER_INTENT_DELAY_MS = 120;

/* ease-in-out cubic: the track leaves and rejoins full speed gently, so neither edge of the
   hover reads as a jolt */
function easeInOutCubic(progress: number): number {
	return progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
}

type EasedHoverPause = Readonly<{
	/**
	 * Whether the ramp is driving the hover pause. False until the animation is found (SSR,
	 * pre-hydration, engines without WAAPI), where the CSS hover pause has to cover instead.
	 */
	active: boolean;
	onPointerEnter: () => void;
	onPointerLeave: () => void;
}>;

/**
 * Softens the marquee's hover pause: rather than the animation stopping and restarting dead,
 * its playback rate eases between full speed and a standstill. The rate (not
 * animation-play-state) is what's animated - play-state only flips instantly, and it stays
 * free for the play/pause control and the reduced-motion CSS to own.
 */
export function useEasedHoverPause(trackRef: RefObject<HTMLElement | null>): EasedHoverPause {
	const [active, setActive] = useState(false);
	const frameRef = useRef<number | undefined>(undefined);
	const hoverIntentRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const cancelHoverIntent = useCallback(() => {
		if (hoverIntentRef.current === undefined) return;
		clearTimeout(hoverIntentRef.current);
		hoverIntentRef.current = undefined;
	}, []);

	const rampTo = useCallback(
		(target: number) => {
			const animation = trackRef.current ? findMarqueeAnimation(trackRef.current) : undefined;
			if (!animation) return;
			if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);

			const from = animation.playbackRate;
			const distance = Math.abs(target - from);
			if (distance === 0) return;
			// a ramp reversed midway has less ground to cover, so it takes proportionally less time
			// - otherwise the return trip would crawl compared to the one it interrupted
			const duration = RAMP_DURATION_MS * distance;
			const start = performance.now();

			const step = (now: number): void => {
				const progress = Math.min((now - start) / duration, 1);
				animation.playbackRate = from + (target - from) * easeInOutCubic(progress);
				frameRef.current = progress < 1 ? requestAnimationFrame(step) : undefined;
			};
			frameRef.current = requestAnimationFrame(step);
		},
		[trackRef],
	);

	useEffect(() => {
		const track = trackRef.current;
		if (!track) return undefined;

		const tryActivate = (): boolean => {
			if (!findMarqueeAnimation(track)) return false;
			setActive(true);
			// the pointer can already be inside when we take over from the CSS pause (hovering across
			// hydration); without this the track would resume under a stationary cursor
			if (track.matches(':hover')) rampTo(0);
			return true;
		};

		if (tryActivate()) return undefined;

		/* a track hidden at the current breakpoint (the mobile-only row on desktop) runs no
		   animation at all, so there is nothing to discover until it becomes visible. Without a
		   retry, resizing down past the breakpoint would leave that row on the CSS hard pause for
		   the rest of the session. The listener drops itself the moment discovery succeeds. */
		const onResize = (): void => {
			if (tryActivate()) window.removeEventListener('resize', onResize);
		};
		window.addEventListener('resize', onResize);

		return () => window.removeEventListener('resize', onResize);
	}, [trackRef, rampTo]);

	useEffect(
		() => () => {
			if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
			cancelHoverIntent();
		},
		[cancelHoverIntent],
	);

	return {
		active,
		onPointerEnter: useCallback(() => {
			cancelHoverIntent();
			hoverIntentRef.current = setTimeout(() => {
				hoverIntentRef.current = undefined;
				rampTo(0);
			}, HOVER_INTENT_DELAY_MS);
		}, [cancelHoverIntent, rampTo]),
		/* a pointer that leaves before the dwell elapses never started a ramp, and rampTo no-ops
		   on a track already at full speed - so cancelling is all a crossing costs */
		onPointerLeave: useCallback(() => {
			cancelHoverIntent();
			rampTo(1);
		}, [cancelHoverIntent, rampTo]),
	};
}
